import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Loader2, CreditCard, Building2, ShieldCheck, Download, Sparkles, AlertTriangle, CheckCircle2, Copy, X } from 'lucide-react';
import { toast } from 'sonner';

export default function FacturacionTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);

  const cargar = () => {
    setLoading(true);
    api.get('/admin/facturacion').then(r => setData(r.data)).catch(() => toast.error('Error al cargar')).finally(() => setLoading(false));
  };
  useEffect(() => { cargar(); }, []);

  if (loading || !data) return <p className="text-sm text-zinc-500 text-center py-8">Cargando...</p>;

  const { plan, num_empleados, preview_mensual, preview_anual, suscripcion, facturas, datos_fiscales, empresa } = data;
  const suscActiva = suscripcion?.estado === 'activa';

  return (
    <div className="space-y-6" data-testid="facturacion-tab">
      {/* Resumen */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base">Plan actual</CardTitle>
          </CardHeader>
          <CardContent>
            {suscActiva ? (
              <>
                <p className="text-3xl font-bold text-zinc-900">{suscripcion.ciclo === 'anual' ? 'Anual' : 'Mensual'}</p>
                <p className="text-sm text-zinc-500 mt-1">Método: <b>{(suscripcion.metodo_pago || '').toUpperCase()}</b></p>
                <p className="text-sm text-zinc-500">Próximo cobro: {(suscripcion.proximo_cobro_at || '').slice(0, 10)}</p>
                {suscripcion.ciclo === 'anual' && empresa.max_empleados > 0 && (
                  <p className="text-sm mt-2">Asientos: <b>{num_empleados} / {empresa.max_empleados}</b>
                    {data.asientos_disponibles === 0 && <span className="text-red-600 ml-2">· TOPE ALCANZADO</span>}
                  </p>
                )}
              </>
            ) : (
              <>
                <p className="text-2xl font-semibold text-amber-700">Sin plan activo</p>
                <p className="text-sm text-zinc-500 mt-2">{num_empleados} empleado{num_empleados !== 1 ? 's' : ''} · {plan.precio_mensual_por_usuario} €/mes/usuario</p>
                <Button onClick={() => setWizardOpen(true)} className="mt-4 bg-zinc-900" data-testid="btn-elegir-plan"><Sparkles className="h-4 w-4 mr-2" /> Elegir un plan</Button>
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base">Cálculo actual</CardTitle>
            <CardDescription>Basado en {num_empleados} empleado{num_empleados !== 1 ? 's' : ''} activos</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span>Mensual (con IVA)</span><b>{preview_mensual.total.toFixed(2)} €</b></div>
            <div className="flex justify-between text-emerald-700"><span>Anual (con IVA, ahorro {plan.descuento_anual_pct}%)</span><b>{preview_anual.total.toFixed(2)} €</b></div>
          </CardContent>
        </Card>
      </div>

      {/* Datos fiscales */}
      <DatosFiscalesCard datos={datos_fiscales} onSaved={cargar} />

      {/* Historial de facturas */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-base">Historial de facturas</CardTitle>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-zinc-500"><tr>
              <th className="text-left py-2">Nº</th><th className="text-left py-2">Fecha</th><th className="text-left py-2">Método</th><th className="text-left py-2">Total</th><th className="text-left py-2">Estado</th><th></th>
            </tr></thead>
            <tbody className="divide-y divide-zinc-100">
              {facturas.map(f => (
                <tr key={f.id} data-testid={`factura-${f.numero}`}>
                  <td className="py-2 font-mono">{f.numero}</td>
                  <td className="py-2 text-zinc-500">{(f.created_at || '').slice(0, 10)}</td>
                  <td className="py-2">{(f.metodo_pago || '').toUpperCase()}</td>
                  <td className="py-2 font-semibold">{f.importes?.total?.toFixed(2)} €</td>
                  <td className="py-2">
                    <Badge variant="outline" className={f.estado === 'pagada' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}>{f.estado}</Badge>
                  </td>
                  <td className="py-2 text-right">
                    <Button size="icon" variant="ghost" onClick={() => descargar(f.id, f.numero)} className="h-7 w-7" title="Descargar PDF"><Download className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}
              {facturas.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-zinc-400">No tienes facturas emitidas.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Ampliar asientos (solo plan anual activo) */}
      {suscActiva && suscripcion.ciclo === 'anual' && <AmpliarAsientosCard data={data} onSaved={cargar} />}

      {/* Cancelación */}
      {suscActiva && <CancelarSuscripcionCard onSaved={cargar} />}

      {/* Wizard */}
      {wizardOpen && <CheckoutWizard data={data} onClose={() => { setWizardOpen(false); cargar(); }} />}
    </div>
  );
}

async function descargar(id, numero) {
  try {
    const res = await api.get(`/admin/facturas/${id}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a'); a.href = url; a.download = `factura_${numero}.pdf`; a.click();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error('No se pudo descargar'); }
}

function DatosFiscalesCard({ datos, onSaved }) {
  const [f, setF] = useState(datos || { razon_social: '', cif: '', direccion: '', codigo_postal: '', ciudad: '', provincia: '', pais: 'España' });
  const [saving, setSaving] = useState(false);
  const prefilled = !!datos?._prefilled;
  const completo = f.cif && f.razon_social && f.direccion && f.codigo_postal && f.ciudad;
  const guardar = async () => {
    setSaving(true);
    try {
      // No enviar el flag _prefilled al backend
      const { _prefilled, ...payload } = f;
      await api.put('/admin/datos-fiscales', payload);
      toast.success('Datos fiscales guardados'); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-heading text-base flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Datos fiscales para facturación</CardTitle>
        <CardDescription>Obligatorios para emitir factura legal. Se muestran en todas las facturas.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {prefilled && (
          <div className="rounded-md border border-blue-200 bg-blue-50 text-blue-900 px-3 py-2 text-sm flex items-start gap-2" data-testid="fisc-prefilled-notice">
            <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              Hemos rellenado automáticamente los campos disponibles con los datos del alta de tu empresa.
              <b> Completa lo que falte y pulsa guardar</b> para confirmar tus datos fiscales.
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[['razon_social','Razón social'],['cif','CIF/NIF'],['direccion','Dirección'],['codigo_postal','Código postal'],['ciudad','Ciudad'],['provincia','Provincia']].map(([k, lbl]) => (
            <div key={k}>
              <Label>{lbl} {prefilled && f[k] ? <span className="text-[10px] text-blue-700 font-normal ml-1">· del alta</span> : null}</Label>
              <Input value={f[k] || ''} onChange={e => setF({ ...f, [k]: e.target.value })} data-testid={`fisc-${k}`} />
            </div>
          ))}
        </div>
        <Button onClick={guardar} disabled={saving || !completo} className="bg-zinc-900" data-testid="guardar-fiscales">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Guardar datos fiscales
        </Button>
        {!completo && <p className="text-xs text-amber-600">Completa todos los campos para poder pagar.</p>}
      </CardContent>
    </Card>
  );
}

function CheckoutWizard({ data, onClose }) {
  const [ciclo, setCiclo] = useState('mensual');
  const [metodo, setMetodo] = useState('tarjeta');
  const [promocode, setPromocode] = useState('');
  const [promoValidated, setPromoValidated] = useState(null);
  const [iban, setIban] = useState('');
  const [mandatoAceptado, setMandatoAceptado] = useState(false);
  const [numAsientos, setNumAsientos] = useState(Math.max(1, data.num_empleados || 1));
  const [enviando, setEnviando] = useState(false);
  const [step, setStep] = useState(1);
  const [resultado, setResultado] = useState(null);

  // Preview: si es anual, recalcular con el numAsientos elegido
  const precioUnit = data.plan.precio_mensual_por_usuario;
  const dctoAnual = data.plan.descuento_anual_pct || 0;
  const ivaPct = data.plan.iva_pct || 21;
  const baseMensual = precioUnit * (data.num_empleados || 0);
  const baseAnual = precioUnit * numAsientos * 12 * (1 - dctoAnual / 100);
  const previewMensual = { total: +(baseMensual * (1 + ivaPct / 100)).toFixed(2) };
  const previewAnual = { total: +(baseAnual * (1 + ivaPct / 100)).toFixed(2) };
  const preview = ciclo === 'anual' ? previewAnual : previewMensual;
  const totalConPromo = promoValidated ? preview.total * (1 - promoValidated.descuento_pct / 100) : preview.total;

  const validarPromo = async () => {
    if (!promocode.trim()) return;
    try {
      const r = await api.get(`/admin/promocodes/${promocode.trim().toUpperCase()}/validar`);
      setPromoValidated(r.data); toast.success(`Código aplicado: -${r.data.descuento_pct}%`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Código no válido'); setPromoValidated(null); }
  };

  const pagar = async () => {
    if (metodo === 'sepa' && (!iban.trim() || !mandatoAceptado)) {
      toast.error('Introduce el IBAN y acepta el mandato SEPA'); return;
    }
    if (ciclo === 'anual' && numAsientos < data.num_empleados) {
      toast.error(`Debes contratar al menos ${data.num_empleados} asientos (ya tienes ese número de usuarios activos).`); return;
    }
    setEnviando(true);
    try {
      const payload = { ciclo, metodo, origin_url: window.location.origin };
      if (ciclo === 'anual') payload.num_asientos_contratados = numAsientos;
      if (promocode) payload.promocode = promocode.trim().toUpperCase();
      if (metodo === 'sepa') { payload.iban = iban; payload.mandato_aceptado = true; }
      const r = await api.post('/admin/checkout', payload);
      setResultado(r.data);
      if (metodo === 'tarjeta' && r.data.checkout_url) {
        window.location.href = r.data.checkout_url; return;
      }
      setStep(3);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al iniciar el pago'); }
    finally { setEnviando(false); }
  };

  const banco = data.datos_bancarios_emisor || {};

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Elegir plan y pagar</DialogTitle>
          <DialogDescription>{step === 1 ? 'Paso 1 de 2 — Elige ciclo y método de pago' : step === 2 ? 'Paso 2 de 2 — Confirma y paga' : 'Instrucciones de pago'}</DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-5">
            <div>
              <Label className="text-sm font-semibold mb-2 block">Ciclo</Label>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => setCiclo('mensual')} data-testid="ciclo-mensual"
                  className={`p-4 rounded-xl border-2 text-left ${ciclo === 'mensual' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200'}`}>
                  <p className="text-xs uppercase text-zinc-500">Mensual</p>
                  <p className="text-2xl font-bold">{previewMensual.total.toFixed(2)} €</p>
                  <p className="text-xs text-zinc-500">IVA incluido · se cobra {data.num_empleados} usuario{data.num_empleados !== 1 ? 's' : ''} × precio</p>
                </button>
                <button onClick={() => setCiclo('anual')} data-testid="ciclo-anual"
                  className={`relative p-4 rounded-xl border-2 text-left ${ciclo === 'anual' ? 'border-emerald-600 bg-emerald-50' : 'border-zinc-200'}`}>
                  <Badge className="absolute top-2 right-2 bg-emerald-600 text-white">-{data.plan.descuento_anual_pct}%</Badge>
                  <p className="text-xs uppercase text-zinc-500">Anual</p>
                  <p className="text-2xl font-bold">{previewAnual.total.toFixed(2)} €</p>
                  <p className="text-xs text-zinc-500">IVA incluido · contratas {numAsientos} asientos por 12 meses</p>
                </button>
              </div>
            </div>

            {ciclo === 'anual' && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-4">
                <Label className="text-sm font-semibold mb-1 block">Asientos a contratar</Label>
                <p className="text-xs text-zinc-600 mb-2">
                  Elige cuántos usuarios activos podrá tener tu empresa este año.
                  Actualmente tienes <b>{data.num_empleados}</b>. Podrás ampliar más adelante pagando la parte proporcional.
                </p>
                <div className="flex items-center gap-3">
                  <Input type="number" min={Math.max(1, data.num_empleados || 1)} value={numAsientos}
                    onChange={e => setNumAsientos(Math.max(1, parseInt(e.target.value) || 1))} className="max-w-[160px]"
                    data-testid="num-asientos" />
                  <span className="text-sm text-zinc-600">= {(precioUnit * numAsientos * 12 * (1 - dctoAnual / 100)).toFixed(2)} € + IVA</span>
                </div>
              </div>
            )}

            <div>
              <Label className="text-sm font-semibold mb-2 block">Método de pago</Label>
              <div className="grid grid-cols-3 gap-3">
                {[['tarjeta','Tarjeta',ciclo === 'mensual' ? 'Renovación auto' : 'Con Stripe'],['transferencia','Transferencia','Manual'],['sepa','Domiciliación','SEPA']].map(([k, lbl, sub]) => (
                  <button key={k} onClick={() => setMetodo(k)} data-testid={`metodo-${k}`}
                    className={`p-3 rounded-lg border-2 text-center ${metodo === k ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200'}`}>
                    <p className="font-semibold text-sm">{lbl}</p>
                    <p className="text-xs text-zinc-500">{sub}</p>
                  </button>
                ))}
              </div>
              {ciclo === 'mensual' && metodo !== 'tarjeta' && (
                <p className="text-xs text-amber-700 mt-2">
                  ⚠ Con pago mensual + {metodo === 'sepa' ? 'SEPA' : 'transferencia'}, cada mes recibirás una factura nueva.
                  {metodo === 'sepa' ? ' El importe se cargará automáticamente en tu cuenta.' : ' Deberás pagar manualmente cada mes.'}
                </p>
              )}
            </div>
            <div>
              <Label className="text-sm font-semibold mb-2 block">Código promocional (opcional)</Label>
              <div className="flex gap-2">
                <Input value={promocode} onChange={e => setPromocode(e.target.value.toUpperCase())} placeholder="EJEMPLO25" data-testid="promocode" />
                <Button variant="outline" onClick={validarPromo}>Aplicar</Button>
              </div>
              {promoValidated && <p className="text-xs text-emerald-700 mt-1">✓ -{promoValidated.descuento_pct}% aplicado. Total: {totalConPromo.toFixed(2)} €</p>}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancelar</Button>
              <Button onClick={() => setStep(2)} className="bg-zinc-900" data-testid="ir-a-confirmar">Continuar</Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="rounded-lg bg-zinc-50 border border-zinc-200 p-4 space-y-1 text-sm">
              <div className="flex justify-between"><span>Ciclo</span><b>{ciclo}</b></div>
              <div className="flex justify-between"><span>Método</span><b>{metodo}</b></div>
              <div className="flex justify-between text-lg pt-2 border-t border-zinc-200 mt-2"><span>Total a pagar</span><b>{totalConPromo.toFixed(2)} € (IVA incluido)</b></div>
            </div>

            {metodo === 'sepa' && (
              <div className="space-y-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
                <div><Label className="text-sm">IBAN de la cuenta a domiciliar</Label>
                  <Input value={iban} onChange={e => setIban(e.target.value.toUpperCase())} placeholder="ES91 2100 0418 4502 0005 1332" data-testid="sepa-iban" /></div>
                <label className="flex items-start gap-2 text-xs text-zinc-700">
                  <input type="checkbox" checked={mandatoAceptado} onChange={e => setMandatoAceptado(e.target.checked)} data-testid="sepa-mandato" className="mt-0.5" />
                  <span>Autorizo a <b>{banco.razon_social || 'el emisor'}</b> a enviar instrucciones a mi entidad para adeudar en la cuenta indicada el importe correspondiente a mi suscripción, según el mandato SEPA CORE. Este mandato queda firmado electrónicamente con mi IP y fecha actual.</span>
                </label>
              </div>
            )}

            {metodo === 'transferencia' && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 space-y-1">
                <p><b>Tras confirmar</b> se emitirá la factura como <b>pendiente</b>. Realiza la transferencia a:</p>
                <p className="font-mono text-xs mt-2">IBAN: {banco.iban || '—'}<br/>Titular: {banco.razon_social || '—'}<br/>Banco: {banco.banco || '—'}</p>
                <p className="text-xs mt-2">Cuando confirmemos el ingreso, tu suscripción se activará automáticamente. Suele tardar entre 1 y 2 días laborables.</p>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(1)}>Atrás</Button>
              <Button onClick={pagar} disabled={enviando} className="bg-zinc-900" data-testid="confirmar-pago">
                {enviando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                {metodo === 'tarjeta' ? 'Pagar con tarjeta' : metodo === 'sepa' ? 'Firmar mandato SEPA' : 'Emitir factura pendiente'}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 3 && resultado && (
          <div className="space-y-3">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
            <h3 className="font-heading text-lg font-bold text-center">Factura {resultado.numero} emitida</h3>
            {metodo === 'transferencia' && (
              <p className="text-sm text-zinc-600 text-center">Realiza la transferencia con el concepto <b>{resultado.numero}</b>. Cuando confirmemos el ingreso activaremos tu suscripción.</p>
            )}
            {metodo === 'sepa' && (
              <p className="text-sm text-zinc-600 text-center">Mandato SEPA firmado correctamente. El cobro se realizará en el próximo ciclo de facturación.</p>
            )}
            <DialogFooter><Button onClick={onClose}>Cerrar</Button></DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AmpliarAsientosCard({ data, onSaved }) {
  const [open, setOpen] = useState(false);
  const [cantidad, setCantidad] = useState(1);
  const [metodo, setMetodo] = useState('tarjeta');
  const [iban, setIban] = useState('');
  const [mandato, setMandato] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const banco = data.datos_bancarios_emisor || {};
  const fin = data.empresa?.plan_ciclo_end;
  const diasRest = fin ? Math.max(1, Math.round((new Date(fin) - new Date()) / 86400000)) : null;
  const mesesRest = diasRest ? Math.round((diasRest / 30) * 100) / 100 : null;
  const precioUnit = data.plan.precio_mensual_por_usuario;
  const dcto = data.plan.descuento_anual_pct || 0;
  const iva = data.plan.iva_pct || 21;
  const base = mesesRest ? precioUnit * cantidad * mesesRest * (1 - dcto / 100) : 0;
  const total = +(base * (1 + iva / 100)).toFixed(2);

  const enviar = async () => {
    if (metodo === 'sepa' && (!iban.trim() || !mandato)) {
      toast.error('IBAN y mandato SEPA obligatorios'); return;
    }
    setEnviando(true);
    try {
      const payload = { cantidad: parseInt(cantidad), metodo, origin_url: window.location.origin };
      if (metodo === 'sepa') { payload.iban = iban; payload.mandato_aceptado = true; }
      const r = await api.post('/admin/plan/asientos-adicionales', payload);
      setResultado(r.data);
      if (metodo === 'tarjeta' && r.data.checkout_url) { window.location.href = r.data.checkout_url; return; }
      toast.success('Solicitud creada. Se activarán al confirmar el pago.');
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setEnviando(false); }
  };

  return (
    <>
      <Card className="border-emerald-200 bg-emerald-50/30">
        <CardHeader>
          <CardTitle className="font-heading text-base flex items-center gap-2"><Sparkles className="h-4 w-4" /> Ampliar asientos</CardTitle>
          <CardDescription>
            Tienes {data.num_empleados} / {data.empresa.max_empleados} asientos usados.
            {mesesRest ? ` Quedan ${mesesRest} meses de tu plan anual.` : ''} Puedes ampliar tu tope pagando solo por los meses restantes.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => setOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ampliar-btn">Ampliar asientos</Button>
        </CardContent>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Ampliar asientos del plan anual</DialogTitle>
            <DialogDescription>Se cobra proporcional a los {mesesRest} meses restantes del año.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Asientos adicionales</Label>
              <Input type="number" min={1} value={cantidad} onChange={e => setCantidad(Math.max(1, parseInt(e.target.value) || 1))} data-testid="ampliar-cantidad" />
            </div>
            <div className="rounded-lg bg-zinc-50 border border-zinc-200 p-3 text-sm">
              <div className="flex justify-between"><span>Base ({cantidad} × {precioUnit} €/mes × {mesesRest} meses × (1-{dcto}%))</span><b>{base.toFixed(2)} €</b></div>
              <div className="flex justify-between text-zinc-500"><span>IVA {iva}%</span><span>{(total - base).toFixed(2)} €</span></div>
              <div className="flex justify-between font-bold pt-2 border-t border-zinc-200 mt-2"><span>Total</span><span>{total.toFixed(2)} €</span></div>
            </div>
            <div>
              <Label>Método de pago</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {[['tarjeta','Tarjeta'],['transferencia','Transferencia'],['sepa','SEPA']].map(([k, lbl]) => (
                  <button key={k} onClick={() => setMetodo(k)} data-testid={`ampliar-metodo-${k}`}
                    className={`p-2 rounded-lg border-2 text-sm ${metodo === k ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200'}`}>{lbl}</button>
                ))}
              </div>
            </div>
            {metodo === 'sepa' && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-2">
                <div><Label className="text-xs">IBAN</Label>
                  <Input value={iban} onChange={e => setIban(e.target.value.toUpperCase())} placeholder="ES91..." data-testid="ampliar-iban" /></div>
                <label className="flex items-start gap-2 text-xs">
                  <input type="checkbox" checked={mandato} onChange={e => setMandato(e.target.checked)} data-testid="ampliar-mandato" />
                  Autorizo el cargo por importe de {total.toFixed(2)} € en la cuenta indicada según mandato SEPA CORE.
                </label>
              </div>
            )}
            {(metodo === 'transferencia' || metodo === 'sepa') && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                Se emitirá una factura pendiente. Los asientos se activarán en cuanto el superadmin confirme la recepción del pago.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={enviar} disabled={enviando || !mesesRest} className="bg-zinc-900" data-testid="ampliar-confirm">
              {enviando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {metodo === 'tarjeta' ? 'Pagar ahora' : 'Emitir factura'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CancelarSuscripcionCard({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [comentario, setComentario] = useState('');
  const cancelar = async () => {
    if (!motivo) { toast.error('Elige un motivo'); return; }
    await api.post('/admin/cancelar-suscripcion', { motivo, comentario });
    toast.success('Suscripción cancelada'); setOpen(false); onSaved?.();
  };
  return (
    <>
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="font-heading text-base text-red-700 flex items-center gap-2"><X className="h-4 w-4" /> Cancelar suscripción</CardTitle>
          <CardDescription>Tu cuenta quedará suspendida al final del periodo pagado. Podrás reactivarla contactando con soporte.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={() => setOpen(true)} className="border-red-300 text-red-700 hover:bg-red-50" data-testid="cancelar-btn">Cancelar mi suscripción</Button>
        </CardContent>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cancelar suscripción</DialogTitle><DialogDescription>Ayúdanos a mejorar contándonos el motivo.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Motivo</Label>
              <select value={motivo} onChange={e => setMotivo(e.target.value)} data-testid="cancel-motivo"
                className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm">
                <option value="">Elige un motivo</option>
                <option value="precio">Precio</option>
                <option value="funciones_faltan">Faltan funcionalidades</option>
                <option value="cambio_proveedor">Cambio a otro proveedor</option>
                <option value="cierre_empresa">Cierre de la empresa</option>
                <option value="otros">Otros</option>
              </select></div>
            <div><Label>Comentario (opcional)</Label>
              <Textarea value={comentario} onChange={e => setComentario(e.target.value)} rows={3} data-testid="cancel-comentario" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Volver</Button>
            <Button onClick={cancelar} className="bg-red-600 hover:bg-red-700" data-testid="cancel-confirm">Sí, cancelar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
